import { GameStateData, CardData } from '../types/index.js';
import { CardRenderer } from './CardRenderer.js';

export class GameView {
  private stockElement: HTMLElement;
  private wasteElement: HTMLElement;
  private foundationElements: HTMLElement[] = [];
  private tableauElements: HTMLElement[] = [];

  constructor() {
    this.stockElement = document.getElementById('stock-pile')!;
    this.wasteElement = document.getElementById('waste-pile')!;

    // Initialize foundation piles
    for (let i = 0; i < 4; i++) {
      const element = document.getElementById(`foundation-${i}`)!;
      this.foundationElements.push(element);
    }

    // Initialize tableau piles
    for (let i = 0; i < 7; i++) {
      const element = document.getElementById(`tableau-${i}`)!;
      this.tableauElements.push(element);
    }
  }

  /**
   * Render the complete game state
   */
  render(state: GameStateData): void {
    this.renderStock(state.stock);
    this.renderWaste(state.waste);
    this.renderFoundations(state.foundations);
    this.renderTableau(state.tableau);
  }

  /**
   * Render stock pile
   */
  private renderStock(stock: CardData[]): void {
    this.stockElement.innerHTML = '';

    if (stock.length === 0) {
      // Empty stock - show placeholder or reset indicator
      const placeholder = CardRenderer.createPlaceholderElement('stock');
      placeholder.classList.add('stock-empty');
      placeholder.innerHTML = '<span class="reload-icon">↻</span>';
      this.stockElement.appendChild(placeholder);
    } else {
      // Show top card (back)
      const topCard = stock[stock.length - 1];
      const cardElement = CardRenderer.createCardElement(topCard);
      cardElement.classList.add('stock-card');
      this.stockElement.appendChild(cardElement);

      // Add card count indicator
      const count = document.createElement('div');
      count.className = 'card-count';
      count.textContent = stock.length.toString();
      this.stockElement.appendChild(count);
    }
  }

  /**
   * Render waste pile
   */
  private renderWaste(waste: CardData[]): void {
    this.wasteElement.innerHTML = '';

    if (waste.length === 0) {
      const placeholder = CardRenderer.createPlaceholderElement('waste');
      this.wasteElement.appendChild(placeholder);
    } else {
      // Show top 3 cards (or all if less than 3)
      const visibleCount = Math.min(3, waste.length);
      const startIndex = waste.length - visibleCount;

      for (let i = startIndex; i < waste.length; i++) {
        const cardElement = CardRenderer.createCardElement(waste[i]);
        cardElement.classList.add('waste-card');
        cardElement.style.setProperty('--card-index', (i - startIndex).toString());
        this.wasteElement.appendChild(cardElement);
      }
    }
  }

  /**
   * Render all foundation piles
   */
  private renderFoundations(foundations: CardData[][]): void {
    foundations.forEach((foundation, index) => {
      this.renderFoundation(foundation, index);
    });
  }

  /**
   * Render a single foundation pile
   */
  private renderFoundation(foundation: CardData[], index: number): void {
    const element = this.foundationElements[index];
    element.innerHTML = '';

    if (foundation.length === 0) {
      const placeholder = CardRenderer.createPlaceholderElement('foundation');
      element.appendChild(placeholder);
    } else {
      // Show only the top card
      const topCard = foundation[foundation.length - 1];
      const cardElement = CardRenderer.createCardElement(topCard);
      element.appendChild(cardElement);
    }
  }

  /**
   * Render all tableau piles
   */
  private renderTableau(tableau: CardData[][]): void {
    tableau.forEach((pile, index) => {
      this.renderTableauPile(pile, index);
    });
  }

  /**
   * Render a single tableau pile
   */
  private renderTableauPile(pile: CardData[], index: number): void {
    const element = this.tableauElements[index];
    element.innerHTML = '';

    if (pile.length === 0) {
      const placeholder = CardRenderer.createPlaceholderElement('tableau');
      element.appendChild(placeholder);
    } else {
      // Show all cards in cascade
      pile.forEach((card, cardIndex) => {
        const cardElement = CardRenderer.createCardElement(card);
        cardElement.classList.add('tableau-card');
        cardElement.style.setProperty('--card-index', cardIndex.toString());
        cardElement.dataset.tableauIndex = index.toString();
        cardElement.dataset.cardIndexInPile = cardIndex.toString();
        element.appendChild(cardElement);
      });
    }
  }

  /**
   * Get card element by ID
   */
  getCardElement(cardId: string): HTMLElement | null {
    return document.querySelector(`[data-card-id="${cardId}"]`);
  }

  /**
   * Highlight valid drop targets
   */
  highlightDropTargets(valid: boolean, elements: HTMLElement[]): void {
    const className = valid ? 'drop-target-valid' : 'drop-target-invalid';

    elements.forEach(element => {
      element.classList.add(className);
    });
  }

  /**
   * Clear all drop target highlights
   */
  clearDropTargetHighlights(): void {
    document.querySelectorAll('.drop-target-valid, .drop-target-invalid').forEach(element => {
      element.classList.remove('drop-target-valid', 'drop-target-invalid');
    });
  }

  /**
   * Add dragging class to element
   */
  setDragging(element: HTMLElement, dragging: boolean): void {
    if (dragging) {
      element.classList.add('card-dragging');
    } else {
      element.classList.remove('card-dragging');
    }
  }

  /**
   * Update move count display
   */
  updateMoveCount(count: number): void {
    const element = document.getElementById('moves-count');
    if (element) {
      element.textContent = `Moves: ${count}`;
    }
  }

  /**
   * Update time elapsed display
   */
  updateTimeElapsed(startTime: number): void {
    const element = document.getElementById('time-elapsed');
    if (element) {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      element.textContent = `Time: ${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  }
}
